<template>
  <div class="container">
    <ViewHeader :title="$t('message.storeLocator.extraInfo.title')">
      <ui-button
        class="ml-4 mr-auto"
        outlined
        icon="mdi-chevron-left"
        @click="goToStoreLocator"
      >
        {{ $t("message.storeLocator.backToStoreLocator") }}
      </ui-button>
    </ViewHeader>
    <v-card flat class="section-container">
      <PageHeader :title="$t('message.storeLocator.configSubtitle')">
        <div v-if="edited" class="header__buttons">
          <ui-button outlined color="primary" @click="cancelUpdate">
            {{ $t("message.common.cancel") }}
          </ui-button>
          <ui-button
            color="primary"
            :loading="extraInfoUpdating"
            @click="updateExtraInfo"
          >
            {{ $t("message.common.save") }}
          </ui-button>
        </div>
      </PageHeader>

      <div class="section section__contact">
        <div class="section-title">
          <div>
            {{ $t("message.storeLocator.extraInfo.contact") }}
          </div>
        </div>
        <v-text-field
          v-model="email"
          :label="$t('message.common.emailAddress')"
          class="text-field"
          @input="edited = true"
        ></v-text-field>
      </div>
      <div class="section section__contact">
        <div class="section-title">
          <div>
            {{ $t("message.storeLocator.extraInfo.offerLink") }}
          </div>
        </div>
        <v-text-field
          v-model="offerLink"
          :label="$t('message.common.urlAddress')"
          class="text-field"
          v-validate="'url|protocol'"
          data-vv-name="offerLink"
          :error-messages="collectErrors('offerLink')"
          @input="edited = true"
        ></v-text-field>
      </div>
      <div class="section section__sections">
        <div class="section-title">
          <div>
            {{ $t("message.storeLocator.extraInfo.sections") }}
          </div>
          <div>
            <ui-button
              outlined
              color="primary"
              icon="icon-plus"
              minWidth="200"
              @click="showDialog = true"
            >
              {{ $t("message.common.add") }}
            </ui-button>
          </div>
        </div>
      </div>
      <TransitionGroup
        v-if="sections.length"
        tag="div"
        name="fade"
        class="sections-list"
      >
        <component
          v-for="section in sections"
          :is="section.component"
          :key="section.id"
          :id="section.id"
          :name="section.name"
          :position="section.position"
          :items="section.items"
          :is-deleting="section.isDeleting"
          @delete-section="deleteSection(section)"
          @delete-row="row => deleteRow(row, section)"
          @edit="handleEdit(section)"
          @move-up="moveSectionUp(section)"
          @move-down="moveSectionDown(section)"
        />
      </TransitionGroup>
      <no-data
        v-if="!sections.length && !loading"
        :description="$t('message.storeLocator.extraInfo.noSections')"
      ></no-data>
      <AddSectionDialog
        v-if="showDialog"
        :show="showDialog"
        @close="showDialog = false"
        @created="handleCreated"
      />
      <EditSectionDialog
        v-if="showEditDialog"
        :show="showEditDialog"
        :data="selectedSection"
        @close="showEditDialog = false"
        @created="handleEdited"
      />
      <SectionLoader :status="loading" :opacity="1" />
    </v-card>
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import LinkSection from "@/components/StoreLocator/sections/LinkSection.vue";
import NoData from "@/components/ui/NoData";

import AddSectionDialog from "@/components/StoreLocator/StoreLocatorAddSectionDialog.vue";
import EditSectionDialog from "@/components/StoreLocator/StoreLocatorEditSectionDialog.vue";
import serviceHandler from "@/helpers/handlers/service";

import storeLocatorService from "@/services/storeLocator";

import { SECTION_TYPES } from "@/enums/section-types.enum";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import PageHeader from "@/components/BusinessAddresses/StoreLocatorPageHeader.vue";

const componentMapping = {
  [SECTION_TYPES.LINK_SECTION]: LinkSection
};

export default {
  name: "StoreLocatorConfigTab",
  components: {
    LinkSection,
    SectionLoader,
    AddSectionDialog,
    EditSectionDialog,
    NoData,
    ViewHeader,
    PageHeader
  },

  data() {
    return {
      email: "",
      offerLink: "",
      initialEmail: "",
      offerInitialLink: "",
      extraInfo: {},
      sections: [],
      edited: false,
      showDialog: false,
      showEditDialog: false,
      selectedSection: null,
      extraInfoUpdating: false,
      loading: false,
      facilityId: this.$route.params.id
    };
  },

  async created() {
    this.loading = true;
    await this.fetchExtraInfo();
    this.loading = false;
  },

  methods: {
    goToStoreLocator() {
      this.$router.push({
        name: "store-locator-config",
        params: { id: this.facilityId }
      });
    },
    async fetchExtraInfo() {
      const extraInfo = await serviceHandler(
        storeLocatorService.getExtraInfo(this.facilityId)
      );
      this.initialEmail = extraInfo.email;
      this.email = this.initialEmail;
      this.offerInitialLink = extraInfo.store_offer_url;
      this.offerLink = this.offerInitialLink;
      this.sections = [];
      const sections = await serviceHandler(
        storeLocatorService.getExtraInfoSections(this.facilityId)
      );
      for (const section of sections) {
        this.addSection(section);
      }
    },
    getComponent(section) {
      const elementType = section.items[0].element_type;
      return componentMapping[elementType];
    },
    handleCreated(newSection) {
      this.addSection(newSection);
      this.showDialog = false;
    },
    async updateExtraInfo() {
      this.extraInfoUpdating = true;
      await serviceHandler(
        storeLocatorService.updateExtraInfo(this.facilityId, {
          email: this.email,
          store_offer_url: this.offerLink
        })
      );
      this.extraInfoUpdating = false;
      this.edited = false;
    },
    cancelUpdate() {
      this.email = this.initialEmail;
      this.offerLink = this.offerInitialLink;
      this.edited = false;
    },
    handleEdited(editedSection) {
      this.sections = this.sections.map(section => {
        if (section.id === editedSection.id) {
          return {
            ...section,
            name: editedSection.name,
            items: editedSection.items
          };
        }
        return section;
      });
      this.showEditDialog = false;
    },
    addSection(section) {
      const component = this.getComponent(section);
      this.sections.push({
        id: section.id,
        name: section.name,
        order: section.order,
        items: section.items,
        component
      });
    },
    async changeOrder(section, order) {
      await serviceHandler(
        storeLocatorService.updateExtraInfoSection(
          this.facilityId,
          section.id,
          {
            order
          }
        )
      );
    },
    async deleteSection(selectedSection) {
      this.sections = this.sections.map(section => {
        if (section.id === selectedSection.id) {
          section.isDeleting = true;
        }
        return section;
      });
      await serviceHandler(
        storeLocatorService.deleteSection(this.facilityId, selectedSection.id)
      );
      this.sections = this.sections.filter(s => s.id !== selectedSection.id);
    },
    async deleteRow(row, selectedSection) {
      if (selectedSection.items.length === 1) {
        this.deleteSection(selectedSection);
        return;
      }
      const items = selectedSection.items.filter(item => item.id !== row);
      this.sections = this.sections.map(section => {
        if (section.id === selectedSection.id) {
          return {
            ...section,
            items
          };
        }
        return section;
      });
      await storeLocatorService.updateExtraInfoSection(
        this.facilityId,
        selectedSection.id,
        { items }
      );
    },
    handleEdit(section) {
      this.selectedSection = section;
      this.showEditDialog = true;
    },
    swapSections(currentIndex, targetIndex) {
      const currentSection = this.sections[currentIndex];
      const targetSection = this.sections[targetIndex];
      this.$set(this.sections, currentIndex, targetSection);
      this.$set(this.sections, targetIndex, currentSection);
      this.changeOrder(currentSection, targetSection.order);
      this.changeOrder(targetSection, currentSection.order);
    },
    moveSectionUp(section) {
      const currentIndex = this.sections.findIndex(s => s.id === section.id);
      if (currentIndex === 0) return;
      this.swapSections(currentIndex, currentIndex - 1);
    },
    moveSectionDown(section) {
      const currentIndex = this.sections.findIndex(s => s.id === section.id);
      if (currentIndex === this.sections.length - 1) return;
      this.swapSections(currentIndex, currentIndex + 1);
    },
    collectErrors(field) {
      const errors = this.errors.collect(field);
      return errors.map(error =>
        error.replace(
          field,
          this.$t("message.storeLocator.extraInfo.offerLink")
        )
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.section-container {
  padding: $spacing * 2 $spacing * 4;
}

.header {
  min-height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__buttons {
    display: flex;
    gap: $spacing * 2;
    margin-left: $spacing * 3;
  }
}

.section-title {
  @include sectionTitle();
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section {
  margin-top: $spacing * 4;
  margin-left: $spacing * 2;
}

.sections-list {
  display: flex;
  flex-direction: column;
  gap: $spacing * 2;
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
  position: absolute;
}

.text-field {
  width: 50%;
}
</style>
